import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  UserMinus,
  Crown,
  Mail,
  LayoutGrid,
  List,
  Users,
  RefreshCw,
  X,
  Send,
  ArrowLeft,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  ActionSheet,
  ActionSheetContent,
  ActionSheetItem,
  ActionSheetSeparator,
} from "@/components/ui/bottom-sheet";
import { Button, FloatingActionButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "@/components/ui/mobile-nav";
import { RadioGroup, RadioGroupItem } from "@/components/ui/RadioGroup";
import { HorizontalScroll } from "@/components/ui/responsive-table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { teamApi } from "@/services/api";

// Role configuration with colors
const ROLE_CONFIG = {
  owner: {
    label: "Owner",
    desc: "Full control over the organisation",
    className:
      "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400",
  },
  admin: {
    label: "Admin",
    desc: "Can manage team and settings",
    className:
      "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400",
  },
  manager: {
    label: "Manager",
    desc: "Can manage envelopes and templates",
    className:
      "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400",
  },
  member: {
    label: "Member",
    desc: "Can create and send envelopes",
    className:
      "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
  },
  viewer: {
    label: "Viewer",
    desc: "Can only view documents",
    className: "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400",
  },
};

// Hook for translated roles
const useRoles = () => {
  const { t } = useTranslation();
  return [
    { value: "admin", label: t("roles.admin"), desc: t("roles.adminDesc") },
    {
      value: "manager",
      label: t("roles.manager"),
      desc: t("roles.managerDesc"),
    },
    { value: "member", label: t("roles.member"), desc: t("roles.memberDesc") },
    { value: "viewer", label: t("roles.viewer"), desc: t("roles.viewerDesc") },
  ];
};

// Member card for grid view
const MemberCard = ({ member, onEdit, onRemove, isRemoving }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [actionSheetOpen, setActionSheetOpen] = useState(false);

  const displayName = member.name || member.email || "Unknown";
  const displayEmail = member.email || "";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const roleConfig = ROLE_CONFIG[member.role] || ROLE_CONFIG.member;

  const handleMoreClick = (e) => {
    e.stopPropagation();
    if (isMobile) {
      setActionSheetOpen(true);
    }
  };

  return (
    <>
      <Card className="group hover:shadow-md transition-shadow overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <Avatar className="h-12 w-12">
              {member.avatarUrl ? (
                <AvatarImage src={member.avatarUrl} alt={displayName} />
              ) : null}
              <AvatarFallback className="bg-primary/10 text-primary font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>

            {!member.isOwner &&
              (isMobile ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 touch-target"
                  onClick={handleMoreClick}
                  aria-label={t("common.moreOptions")}
                >
                  <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={t("common.moreOptions")}
                    >
                      <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={onEdit}>
                      <Edit className="h-4 w-4 mr-2" aria-hidden="true" />
                      {t("team.editRole")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={onRemove}
                      disabled={isRemoving}
                      className="text-destructive focus:text-destructive"
                    >
                      <UserMinus className="h-4 w-4 mr-2" aria-hidden="true" />
                      {t("team.removeMember")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ))}
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-foreground truncate">
                {displayName}
              </h3>
              {member.isOwner && (
                <Crown
                  className="h-4 w-4 text-yellow-500 shrink-0"
                  aria-label="Owner"
                />
              )}
            </div>
            <p className="text-sm text-muted-foreground truncate">
              {displayEmail}
            </p>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <Badge variant="secondary" className={roleConfig.className}>
              {roleConfig.label}
            </Badge>
            {member.joinedAt && (
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(member.joinedAt), {
                  addSuffix: true,
                })}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mobile action sheet */}
      <ActionSheet open={actionSheetOpen} onOpenChange={setActionSheetOpen}>
        <ActionSheetContent>
          <ActionSheetItem onClick={onEdit}>
            <Edit className="h-5 w-5 mr-3" aria-hidden="true" />
            {t("team.editRole")}
          </ActionSheetItem>
          <ActionSheetSeparator />
          <ActionSheetItem
            onClick={onRemove}
            disabled={isRemoving}
            className="text-destructive"
          >
            <UserMinus className="h-5 w-5 mr-3" aria-hidden="true" />
            {t("team.removeMember")}
          </ActionSheetItem>
        </ActionSheetContent>
      </ActionSheet>
    </>
  );
};

// Member row for list view
const MemberRow = ({ member, onEdit, onRemove, isRemoving }) => {
  const { t } = useTranslation();
  const displayName = member.name || member.email || "Unknown";
  const displayEmail = member.email || "";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const roleConfig = ROLE_CONFIG[member.role] || ROLE_CONFIG.member;

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            {member.avatarUrl ? (
              <AvatarImage src={member.avatarUrl} alt={displayName} />
            ) : null}
            <AvatarFallback className="bg-primary/10 text-primary text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium">{displayName}</span>
              {member.isOwner && (
                <Crown
                  className="h-3.5 w-3.5 text-yellow-500"
                  aria-label="Owner"
                />
              )}
            </div>
            <p className="text-sm text-muted-foreground">{displayEmail}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="secondary" className={roleConfig.className}>
          {roleConfig.label}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {member.joinedAt
          ? formatDistanceToNow(new Date(member.joinedAt), { addSuffix: true })
          : "—"}
      </TableCell>
      <TableCell className="w-12">
        {!member.isOwner && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={t("common.moreOptions")}
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Edit className="h-4 w-4 mr-2" aria-hidden="true" />
                {t("team.editRole")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onRemove}
                disabled={isRemoving}
                className="text-destructive focus:text-destructive"
              >
                <UserMinus className="h-4 w-4 mr-2" aria-hidden="true" />
                {t("team.removeMember")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
};

// Invitation card for pending invites section
const InvitationCard = ({
  invite,
  onResend,
  onCancel,
  isResending,
  isCancelling,
}) => {
  const { t } = useTranslation();
  const roleConfig = ROLE_CONFIG[invite.role] || ROLE_CONFIG.member;

  return (
    <Card className="border-dashed border-yellow-300 dark:border-yellow-700 bg-yellow-50/50 dark:bg-yellow-900/10">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
            <Mail
              className="h-5 w-5 text-yellow-600 dark:text-yellow-400"
              aria-hidden="true"
            />
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onResend(invite.id)}
              disabled={isResending}
              className="h-8 text-xs"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 mr-1.5 ${isResending ? "animate-spin" : ""}`}
                aria-hidden="true"
              />
              {t("team.resend")}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onCancel(invite.id)}
              disabled={isCancelling}
              className="h-8 w-8 text-destructive hover:text-destructive"
              aria-label={t("team.cancelInvitation")}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        <div className="space-y-1">
          <h3 className="font-medium text-foreground truncate">
            {invite.email}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("team.invitedAs")} {roleConfig.label.toLowerCase()} •{" "}
            {formatDistanceToNow(new Date(invite.createdAt), {
              addSuffix: true,
            })}
          </p>
        </div>

        <div className="mt-3">
          <Badge
            variant="outline"
            className="text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700"
          >
            {t("team.pendingInvitation")}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

// Loading skeleton
const TeamSkeleton = () => {
  const isMobile = useIsMobile();
  return (
    <main className="space-y-6">
      <div className="flex justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        {!isMobile && <Skeleton className="h-10 w-40" />}
      </div>
      {isMobile && <Skeleton className="h-10 w-full" />}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <Skeleton className="h-12 w-12 rounded-full" />
                <Skeleton className="h-8 w-8" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
              </div>
              <Skeleton className="h-6 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    </main>
  );
};

// Empty state
const EmptyState = ({ hasFilters, onClear }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <Users className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        </div>
        <h3 className="text-lg font-semibold mb-1">{t("team.noMembers")}</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {hasFilters ? t("team.noMembersFiltered") : t("team.noMembersYet")}
        </p>
        {hasFilters && (
          <Button
            variant="outline"
            onClick={onClear}
            size="lg"
            className="touch-target"
          >
            {t("common.clearFilters")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Invite member dialog
const InviteDialog = ({ open, onOpenChange, onSuccess }) => {
  const { t } = useTranslation();
  const ROLES = useRoles();
  const [formData, setFormData] = useState({
    emails: "",
    role: "member",
    message: "",
  });
  const [errors, setErrors] = useState({});

  const inviteMutation = useMutation({
    mutationFn: (data) => teamApi.inviteMember(data),
    onSuccess: () => {
      toast.success(t("settings.team.invitationSent"));
      setFormData({ emails: "", role: "member", message: "" });
      setErrors({});
      onSuccess();
      onOpenChange(false);
    },
    onError: (error) => {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        t("settings.team.inviteFailed");
      toast.error(message);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const emails = formData.emails
      .split(/[,\n]/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (emails.length === 0) {
      setErrors({ emails: t("team.enterAtLeastOneEmail") });
      return;
    }

    const invalidEmails = emails.filter((email) => !email.includes("@"));
    if (invalidEmails.length > 0) {
      setErrors({
        emails: `${t("team.invalidEmails")}: ${invalidEmails.join(", ")}`,
      });
      return;
    }

    setErrors({});
    inviteMutation.mutate({
      emails,
      role: formData.role,
      message: formData.message || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("team.inviteMembers")}</DialogTitle>
          <DialogDescription>{t("team.inviteMembersDesc")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="emails">{t("team.emailAddresses")}</Label>
            <Textarea
              id="emails"
              value={formData.emails}
              onChange={(e) =>
                setFormData({ ...formData, emails: e.target.value })
              }
              placeholder={t("placeholders.enterEmailAddresses")}
              rows={3}
              className={errors.emails ? "border-destructive" : ""}
            />
            {errors.emails && (
              <p className="text-destructive text-sm">{errors.emails}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t("team.role")}</Label>
            <RadioGroup
              value={formData.role}
              onValueChange={(value) =>
                setFormData({ ...formData, role: value })
              }
              className="space-y-2"
            >
              {ROLES.map((role) => (
                <label
                  key={role.value}
                  htmlFor="role"
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.role === role.value
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-accent"
                  }`}
                >
                  <RadioGroupItem value={role.value} className="mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">{role.label}</p>
                    <p className="text-sm text-muted-foreground">{role.desc}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{t("team.personalMessage")}</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              placeholder={t("placeholders.addPersonalNote")}
              rows={2}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={inviteMutation.isPending}>
              {inviteMutation.isPending ? (
                <>
                  <RefreshCw
                    className="h-4 w-4 mr-2 animate-spin"
                    aria-hidden="true"
                  />
                  {t("common.sending")}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" aria-hidden="true" />
                  {t("team.sendInvitations")}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit member role dialog
const EditRoleDialog = ({ member, open, onOpenChange, onSuccess }) => {
  const { t } = useTranslation();
  const ROLES = useRoles();
  const [role, setRole] = useState(member?.role || "member");

  const updateMutation = useMutation({
    mutationFn: (data) => teamApi.updateMemberRole(member.id, data.role),
    onSuccess: () => {
      toast.success(t("settings.team.roleUpdated"));
      onSuccess();
      onOpenChange(false);
    },
    onError: (error) => {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        t("settings.team.updateFailed");
      toast.error(message);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate({ role });
  };

  // Reset role when member changes
  if (member && role !== member.role && !updateMutation.isPending) {
    setRole(member.role);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("team.editMemberRole")}</DialogTitle>
          <DialogDescription>{member?.name || member?.email}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <RadioGroup
            value={role}
            onValueChange={setRole}
            className="space-y-2"
          >
            {ROLES.map((r) => (
              <label
                key={r.value}
                htmlFor="role"
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  role === r.value
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-accent"
                }`}
              >
                <RadioGroupItem value={r.value} className="mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">{r.label}</p>
                  <p className="text-sm text-muted-foreground">{r.desc}</p>
                </div>
              </label>
            ))}
          </RadioGroup>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending || role === member?.role}
            >
              {updateMutation.isPending
                ? t("common.saving")
                : t("common.saveChanges")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Remove member confirmation dialog
const RemoveDialog = ({
  member,
  open,
  onOpenChange,
  onConfirm,
  isRemoving,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("team.removeMember")}</DialogTitle>
          <DialogDescription>
            {t("team.removeConfirmation", {
              name: member?.name || member?.email,
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isRemoving}
          >
            {isRemoving ? t("common.removing") : t("team.removeMember")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main component
export const TeamSettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [searchInput, setSearchInput] = useState("");
  const [filter, setFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [removingMember, setRemovingMember] = useState(null);

  // Fetch team members
  const { data: team, isLoading } = useQuery({
    queryKey: ["team-members"],
    queryFn: async () => {
      const res = await teamApi.getMembers();
      const data = res.data?.data || [];
      return data.map((member) => ({
        ...member,
        name: member.user
          ? `${member.user.firstName || ""} ${member.user.lastName || ""}`.trim()
          : null,
        email: member.user?.email || member.email,
        avatarUrl: member.user?.avatarUrl || member.avatarUrl,
        isOwner: member.role === "owner",
        joinedAt: member.createdAt,
      }));
    },
  });

  // Fetch pending invitations
  const { data: invitations } = useQuery({
    queryKey: ["team-invitations"],
    queryFn: () => teamApi.getInvitations().then((res) => res.data?.data || []),
  });

  // Remove member mutation
  const removeMutation = useMutation({
    mutationFn: (memberId) => teamApi.removeMember(memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      toast.success(t("settings.team.memberRemoved"));
      setRemovingMember(null);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message || t("settings.team.removeFailed"),
      );
    },
  });

  // Cancel invitation mutation
  const cancelInviteMutation = useMutation({
    mutationFn: (inviteId) => teamApi.cancelInvitation(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-invitations"] });
      toast.success(t("settings.team.invitationCancelled"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message || t("settings.team.cancelFailed"),
      );
    },
  });

  // Resend invitation mutation
  const resendInviteMutation = useMutation({
    mutationFn: (inviteId) => teamApi.resendInvitation(inviteId),
    onSuccess: () => {
      toast.success(t("settings.team.invitationResent"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message || t("settings.team.resendFailed"),
      );
    },
  });

  const members = team || [];

  // Filter and search
  const filteredMembers = members.filter((m) => {
    const matchesRole = filter === "all" || m.role === filter;
    const matchesSearch =
      !searchInput ||
      m.name?.toLowerCase().includes(searchInput.toLowerCase()) ||
      m.email?.toLowerCase().includes(searchInput.toLowerCase());
    return matchesRole && matchesSearch;
  });

  const hasFilters = filter !== "all" || searchInput;

  const handleClearFilters = () => {
    setFilter("all");
    setSearchInput("");
  };

  const handleRemove = (member) => {
    setRemovingMember(member);
  };

  const confirmRemove = () => {
    if (removingMember) {
      removeMutation.mutate(removingMember.id);
    }
  };

  const renderContent = () => {
    if (filteredMembers.length === 0) {
      return (
        <EmptyState hasFilters={hasFilters} onClear={handleClearFilters} />
      );
    }

    if (isMobile || viewMode === "grid") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredMembers.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              onEdit={() => setEditingMember(member)}
              onRemove={() => handleRemove(member)}
              isRemoving={
                removeMutation.isPending &&
                removeMutation.variables === member.id
              }
            />
          ))}
        </div>
      );
    }

    return (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.member")}</TableHead>
              <TableHead>{t("common.role")}</TableHead>
              <TableHead>{t("common.joined")}</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMembers.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                onEdit={() => setEditingMember(member)}
                onRemove={() => handleRemove(member)}
                isRemoving={
                  removeMutation.isPending &&
                  removeMutation.variables === member.id
                }
              />
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  if (isLoading) return <TeamSkeleton />;

  return (
    <main className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/settings"
            className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border hover:bg-accent transition-colors touch-target"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-heading tracking-tight">
              {t("team.title")}
            </h1>
            <p className="text-muted-foreground">{t("team.subtitle")}</p>
          </div>
        </div>
        {!isMobile && (
          <Button onClick={() => setShowInviteDialog(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("team.inviteMember")}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          {isMobile ? (
            <HorizontalScroll className="pb-0!">
              <SegmentedControl
                value={filter}
                onValueChange={setFilter}
                className="shrink-0"
              >
                <SegmentedControlItem value="all">
                  {t("common.all")}
                </SegmentedControlItem>
                <SegmentedControlItem value="admin">
                  {t("roles.admin")}
                </SegmentedControlItem>
                <SegmentedControlItem value="member">
                  {t("roles.member")}
                </SegmentedControlItem>
              </SegmentedControl>
            </HorizontalScroll>
          ) : (
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList>
                <TabsTrigger value="all">{t("common.all")}</TabsTrigger>
                <TabsTrigger value="admin">{t("roles.admin")}</TabsTrigger>
                <TabsTrigger value="manager">{t("roles.manager")}</TabsTrigger>
                <TabsTrigger value="member">{t("roles.member")}</TabsTrigger>
                <TabsTrigger value="viewer">{t("roles.viewer")}</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              placeholder={t("team.searchMembers")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={isMobile ? "pl-9 w-full h-11" : "pl-9 w-64"}
            />
          </div>
        </div>

        {!isMobile && (
          <div className="flex items-center border rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode("grid")}
              aria-label={t("common.gridView")}
            >
              <LayoutGrid className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode("list")}
              aria-label={t("common.listView")}
            >
              <List className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        )}
      </div>

      {/* Members */}
      {renderContent()}

      {/* Pending Invitations */}
      {invitations?.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">
            {t("team.pendingInvitations")} ({invitations.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {invitations.map((invite) => (
              <InvitationCard
                key={invite.id}
                invite={invite}
                onResend={(id) => resendInviteMutation.mutate(id)}
                onCancel={(id) => cancelInviteMutation.mutate(id)}
                isResending={
                  resendInviteMutation.isPending &&
                  resendInviteMutation.variables === invite.id
                }
                isCancelling={
                  cancelInviteMutation.isPending &&
                  cancelInviteMutation.variables === invite.id
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      {isMobile && (
        <FloatingActionButton onClick={() => setShowInviteDialog(true)}>
          <Plus className="h-6 w-6" aria-hidden="true" />
        </FloatingActionButton>
      )}

      {/* Dialogs */}
      <InviteDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        onSuccess={() =>
          queryClient.invalidateQueries({ queryKey: ["team-invitations"] })
        }
      />

      {editingMember && (
        <EditRoleDialog
          member={editingMember}
          open={!!editingMember}
          onOpenChange={(open) => !open && setEditingMember(null)}
          onSuccess={() =>
            queryClient.invalidateQueries({ queryKey: ["team-members"] })
          }
        />
      )}

      <RemoveDialog
        member={removingMember}
        open={!!removingMember}
        onOpenChange={(open) => !open && setRemovingMember(null)}
        onConfirm={confirmRemove}
        isRemoving={removeMutation.isPending}
      />
    </main>
  );
};

export default TeamSettings;
